import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { css, keyframes, type Theme } from '@emotion/react';
import { createStackBackgroundUri } from 'client/components/Icons/StackBackground';
import { Logo } from 'client/components/Other/Logo';
import { Color, generateShadow } from 'client/Theme';

export interface ModalProps extends DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> {}

export const Modal = ({ children, ...props }: ModalProps) => {
  return (
    <main css={pageStyle} {...props}>
      <div css={backgroundStyle} />
      <Link to="/" css={logoStyle}>
        <Logo width={128} height={38} />
      </Link>
      <article css={dialogStyle}>{children}</article>
    </main>
  );
};

const background = createStackBackgroundUri({ size: 16, primary: 'rgba(255, 255, 255, 0.05)', secondary: 'rgba(255, 255, 255, 0.05)' });
const backgroundColor = createStackBackgroundUri({ size: 16, primary: Color.primary[100], secondary: Color.secondary[100], chance: 0.15 });

const pageStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background-color: black;
`;

const backgroundAnimation = keyframes`
  0% {
    --size: 400px;
  }
  100% {
    --size: 600px;
  }
`;

const backgroundStyle = css`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &::after {
    position: absolute;
    content: '';
    display: block;
    width: 120%;
    height: 180%;
    background-image: ${background};
    background-size: 896px;
    transform: rotate(8deg);
  }

  &::before {
    @property --size {
      syntax: '<length>';
      inherits: false;
      initial-value: 400px;
    }

    position: absolute;
    content: '';
    display: block;
    width: 120%;
    height: 180%;
    background-image: ${backgroundColor};
    background-size: 896px;
    transform: rotate(8deg);

    mask-image: radial-gradient(circle at center, white 100px, transparent var(--size));

    animation: ${backgroundAnimation} 10s infinite alternate ease-in-out;
  }
`;

const logoStyle = css`
  position: absolute;
  top: 32px;
  left: 32px;
  z-index: 100;
`;

const dialogStyle = (theme: Theme) => css`
  padding: 8px;
  width: 500px;
  background-color: ${theme.color.background};
  border-radius: ${theme.borderRadius * 2}px;
  z-index: 1;

  box-shadow: ${generateShadow({ primary: theme.color.primary[100] })};
`;
